import classnames from 'classnames'
import * as React from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { SelectedCoursesIcon } from 'src/assets/icons/customIcons/page-icons/SelectedCourses'
import { stripTranslationMarkers } from 'src/helpers/fns'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { ErrorBoundary } from 'src/views/components/Error'
import ErrorElement from 'src/views/components/ErrorElement'
import Loader from 'src/views/components/Loader'
import SubPageLayout, { type Page } from 'src/views/includes/SubPageLayout'
import { useOpenExportFile } from './useOpenExportFile'

const EMPTY_VALUE = '-'

/** `eduStatus` of an active, studying student — the only status legacy shows in green. */
const EDU_STATUS_ACTIVE = 1

export interface AdministratorStudentChoicesOutletContext {
  readonly coursesPage: Api.AdministratorStudentChoiceCoursesPage | null | undefined
  readonly targetSemesterId: number | null
  readonly mutateCoursesPage: () => Promise<unknown>
}

export default function AdministratorStudentChoicesLayout(): JSX.Element | null {
  return (
    <React.Suspense fallback={<Loader className="m-auto flex" />}>
      <ErrorBoundary errorElement={<ErrorElement />}>
        <PageContent />
      </ErrorBoundary>
    </React.Suspense>
  )
}

function PageContent(): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const { uid, profileId } = useParams()
  const t = useTranslatable()
  const locale = useLocale()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const activeSemester = useActiveSemester()

  const semId = searchParams.get('semId')
  const targetSemesterId: number | null =
    semId != null && semId !== '' ? parseInt(semId, 10) : activeSemester?.id != null ? Number(activeSemester.id) : null

  const { data: coursesPage, mutate: mutateCoursesPage } = useApi({
    endpoint: Api.getAdministratorStudentChoiceCourses,
    params: React.useMemo(
      () => ({
        headers,
        args: { uid: uid!, profileId: profileId! },
        query: targetSemesterId != null ? { targetSemesterId } : undefined,
      }),
      [headers, uid, profileId, targetSemesterId]
    ),
    shouldFetch: uid != null && profileId != null && targetSemesterId != null,
    suspense: false,
  })

  const { data: studentDefinitions } = useApi({
    endpoint: Api.getAdministratorDefinitionsStudents,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const student = coursesPage?.student
  const studentName = stripTranslationMarkers(student?.fullName)
  const eduStatusLabel = studentDefinitions?.statuses.find(
    (status) => status.value === String(student?.eduStatus)
  )?.label

  const subBreadcrumbsItems = [
    {
      page: t('student:students'),
      path: `/${locale}/administrator/users/students`,
    },
    { page: studentName ?? `#${profileId ?? ''}`, path: pathname },
  ]

  const { openExportFile } = useOpenExportFile()
  const openPrintBySemester = React.useCallback(() => {
    if (uid == null || profileId == null) return
    void openExportFile(
      async () => await Api.getAdministratorStudentChoiceHistory3PrintBySemester({ headers, args: { uid, profileId } })
    )
  }, [headers, openExportFile, profileId, uid])

  const pages = React.useMemo<Page[]>(
    () => [
      { title: t(['კურსის დამატება', 'Add course']), link: 'add-course' },
      { title: t(['დასრულებული კურსები', 'Completed Courses']), link: 'completed-courses' },
      { title: t('choice:sorted_courses'), link: 'sorted-courses' },
      // Like old Argus, the printable version opens in a new browser tab and the admin stays on this one.
      { title: t('choice:printable_version'), link: 'printable', onClick: openPrintBySemester },
    ],
    [openPrintBySemester, t]
  )

  const filterContent =
    student != null ? (
      <div className="mt-2 grid min-w-0 grid-cols-1 divide-y divide-borderGrey rounded-card bg-card sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-5">
        <StudentInfoField label={t('faculty:major_faculty')} value={student.faculty?.name ?? EMPTY_VALUE} />
        <StudentInfoField label={t('program:major_programme')} value={formatProgram(student.program)} />
        <StudentInfoField
          label={t('faculty:preferred_major_program')}
          value={formatProgram(student.preferredMajorProgram)}
        />
        <StudentInfoField
          label={t('faculty:preferred_minor_program')}
          value={student.preferredMinorFaculty?.name ?? EMPTY_VALUE}
        />
        <StudentInfoField
          label={t('program:preferred_minor_programme')}
          value={formatProgram(student.preferredMinorProgram)}
        />
      </div>
    ) : null

  const outletContext: AdministratorStudentChoicesOutletContext = React.useMemo(
    () => ({ coursesPage, targetSemesterId, mutateCoursesPage }),
    [coursesPage, targetSemesterId, mutateCoursesPage]
  )

  return (
    <SubPageLayout
      Icon={SelectedCoursesIcon}
      title={studentName ?? '...'}
      titleAddon={
        eduStatusLabel != null ? (
          <span
            className={classnames(
              'ml-3 rounded-md px-2 py-0.5 text-xs font-semibold text-primaryTextColor',
              student?.eduStatus === EDU_STATUS_ACTIVE ? 'bg-lightGreen' : 'bg-lightRed'
            )}
          >
            {eduStatusLabel}
          </span>
        ) : undefined
      }
      filter={filterContent}
      subBreadcrubms={subBreadcrumbsItems}
      pages={pages}
      outletContext={outletContext}
    />
  )
}

function formatProgram(program: Api.AdministratorChoiceStudentProgram | null | undefined): string {
  return program != null ? `${program.name} - ${program.code}` : EMPTY_VALUE
}

function StudentInfoField({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-1 px-6 py-4">
      <span className="text-xs font-semibold uppercase tracking-wide text-captionColor">{label}</span>
      <span className="text-sm text-primaryTextColor">{value}</span>
    </div>
  )
}
